// Display formatting shared by the queue and the ticket screen.
//
// Timestamps cross the wire as ISO 8601 UTC with a literal Z (docs/API.md §1.3) and
// are converted to the viewer's zone here, at render time — the server never does zone
// maths, so this is the only place it happens.

export const STATUS_LABELS = {
  new: 'New',
  open: 'Open',
  pending: 'Pending',
  on_hold: 'On hold',
  solved: 'Solved',
  closed: 'Closed',
};

// The six base statuses, in workflow order. `Object.keys(STATUS_LABELS)` would give the
// same list, but relying on key order for a user-facing sequence is accidental.
export const BASE_STATUSES = ['new', 'open', 'pending', 'on_hold', 'solved', 'closed'];

export const PRIORITY_LABELS = {
  low: 'Low',
  normal: 'Normal',
  high: 'High',
  urgent: 'Urgent',
};

export const CHANNELS = ['phone', 'email', 'api'];

export const CHANNEL_LABELS = {
  phone: 'Phone',
  email: 'Email',
  api: 'API',
};

const DATE_TIME = new Intl.DateTimeFormat(undefined, {
  dateStyle: 'medium',
  timeStyle: 'short',
});

const DATE_ONLY = new Intl.DateTimeFormat(undefined, { dateStyle: 'medium' });

const RELATIVE = new Intl.RelativeTimeFormat(undefined, { numeric: 'auto' });

/** Absolute local time — used wherever the exact moment matters, and as `title` elsewhere. */
export function formatDateTime(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : DATE_TIME.format(date);
}

export function formatDate(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  return Number.isNaN(date.getTime()) ? null : DATE_ONLY.format(date);
}

const RELATIVE_UNITS = [
  ['year', 365 * 24 * 60 * 60],
  ['month', 30 * 24 * 60 * 60],
  ['day', 24 * 60 * 60],
  ['hour', 60 * 60],
  ['minute', 60],
];

/**
 * "3 hours ago" for the queue's Updated column, where scanning matters more than the
 * exact minute. Always paired with the absolute time in a `title` attribute.
 */
export function formatRelative(iso) {
  if (!iso) return null;
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return null;

  const seconds = Math.round((date.getTime() - Date.now()) / 1000);
  const magnitude = Math.abs(seconds);
  if (magnitude < 45) return 'just now';

  for (const [unit, unitSeconds] of RELATIVE_UNITS) {
    if (magnitude >= unitSeconds) {
      return RELATIVE.format(Math.round(seconds / unitSeconds), unit);
    }
  }
  return RELATIVE.format(Math.round(seconds / 60), 'minute');
}

/**
 * The download URL for one attachment, or null when there is nothing to download.
 *
 * A FUNCTION AND NOT A TEMPLATE LITERAL AT THE CALL SITE, and not because the string
 * is complicated. There is no DOM test harness in this repository (a deliberate
 * standing decision), so anything that only exists inside JSX cannot be tested at all
 * — and the two things worth pinning here are exactly the kind that rot silently:
 *
 *   * **The route shape.** The href must stay `/api/tickets/:ref/attachments/:id`,
 *     because that path is what carries the ticket, and the ticket is what the server
 *     joins through to decide whether this caller may have the bytes
 *     (src/services/attachments.js `findTicketAttachment`). An href that dropped the
 *     ticket segment would be a 404 rather than a hole — but only because the server
 *     has no by-id route to fall back to, which is a property of the server that this
 *     side should not be silently depending on.
 *   * **`is_downloadable` is the only thing that produces a link.** Not `is_purged`
 *     inverted, and not the presence of a filename. Three states arrive on the wire —
 *     stored, not stored yet, purged — and only the first is a link. Returning null
 *     for the other two is what keeps a purged attachment from rendering as a live
 *     link that 404s.
 *
 * Both ids are our own integers on the way out; they are still encoded, because
 * "these are always numbers" is precisely the assumption that stops being true.
 */
export function attachmentHref(publicRef, attachment) {
  if (!publicRef || !attachment?.id) return null;
  if (attachment.is_downloadable !== true) return null;
  return `/api/tickets/${encodeURIComponent(publicRef)}/attachments/${encodeURIComponent(attachment.id)}`;
}

/**
 * The URL that renders an attachment as an image, or null if it is not worth asking.
 *
 * `content_type` is the SENDER's declaration and is used here only to decide what to
 * REQUEST. It is not a security judgement and must never be treated as one: the server
 * reads the actual magic bytes (src/attachments/image-type.js) and answers 404 for
 * anything that is not really one of four raster formats. So a script named
 * `screenshot.png` and typed `image/png` gets asked for, gets refused, and falls back
 * to the download link — which is exactly the path `onError` below handles.
 */
export function attachmentInlineHref(publicRef, attachment) {
  const base = attachmentHref(publicRef, attachment);
  if (!base) return null;
  if (attachment.is_purged === true) return null;
  return inlineKind(attachment) ? `${base}/inline` : null;
}

/**
 * `'image'`, `'video'`, or null — which element should try to render this.
 *
 * `content_type` is the SENDER's declaration and is used here only to decide what to
 * REQUEST and which tag to reach for. It is not a security judgement and must never be
 * treated as one: the server reads the actual magic bytes
 * (src/attachments/media-type.js) and answers 404 for anything that is not really one
 * of the handful of formats it will serve. So a script named `clip.mp4` and typed
 * `video/mp4` gets asked for, gets refused, and falls back to the download link — which
 * is exactly the path the `onError` handlers in MessageThread.jsx exist to take.
 *
 * SVG is excluded here as well as on the server, and `.mkv` and HEIC fall out
 * naturally: neither has a `video/` or `image/` type a browser would accept. The server
 * is the rail; this just avoids requests certain to 404.
 */
export function inlineKind(attachment) {
  const declared = typeof attachment?.content_type === 'string'
    ? attachment.content_type.toLowerCase()
    : '';
  if (declared.includes('svg')) return null;
  if (declared.startsWith('image/')) return 'image';
  // Matroska carries `video/x-matroska`; no browser plays it, and the server refuses
  // it, so there is no point rendering a player that will only ever error.
  if (declared.startsWith('video/') && !declared.includes('matroska')) return 'video';
  return null;
}

/** `attachments[].size_bytes` is a bigint string (docs/API.md §1.2), so parse it here only. */
export function formatBytes(sizeBytes) {
  const bytes = Number(sizeBytes);
  if (!Number.isFinite(bytes) || bytes < 0) return null;
  if (bytes < 1024) return `${bytes} B`;
  const units = ['KB', 'MB', 'GB', 'TB'];
  let value = bytes / 1024;
  let unit = 0;
  while (value >= 1024 && unit < units.length - 1) {
    value /= 1024;
    unit += 1;
  }
  return `${value >= 10 ? Math.round(value) : value.toFixed(1)} ${units[unit]}`;
}

// Block-level tags become newlines before the text is extracted, otherwise every
// paragraph of a customer's email runs into the next one.
const BLOCK_BOUNDARY = /<\s*(br\s*\/?|\/\s*(p|div|li|tr|h[1-6]|blockquote))\s*>/gi;

/**
 * Email HTML from customers is untrusted, so it is never handed to
 * `dangerouslySetInnerHTML`. `DOMParser` builds an inert document — scripts do not run
 * and no subresources load — which makes text extraction from it safe.
 */
export function htmlToText(html) {
  if (!html) return '';
  const withBreaks = String(html).replace(BLOCK_BOUNDARY, '\n');
  try {
    const doc = new DOMParser().parseFromString(withBreaks, 'text/html');
    return (doc.body?.textContent ?? '').replace(/\n{3,}/g, '\n\n').trim();
  } catch {
    return '';
  }
}

/**
 * What the thread shows, and what "show quoted text" expands to.
 * `body_stripped` is the quote-trimmed display copy; `body_html` is the untrimmed
 * original (docs/API.md §3.2).
 */
export function messageBodies(message) {
  // Rendered as text, so markup arriving here shows up as source. The server promises
  // `body_stripped` is plain text (src/mail/quote-trim.js) and for a while it did not
  // keep that promise for HTML-only mail — which is what every phone and webmail client
  // sends. Rows written during that window are still in the table, and re-checking here
  // repairs them on read instead of by migration.
  //
  // The tag test needs a letter, `!` or `/` after the `<`, so a customer writing
  // "if x < y > z" or "<3" is left alone; only something already shaped like markup is
  // put through the converter, and the converter is lossless for text without tags.
  const rawStripped = message.body_stripped?.trim() ?? '';
  const stripped = /<[a-z!/][^>]*>/i.test(rawStripped) ? htmlToText(rawStripped) : rawStripped;
  const text = message.body_text?.trim() ?? '';
  const fromHtml = htmlToText(message.body_html);

  const display = stripped || text || fromHtml;
  const full = fromHtml || text || stripped;
  const hasQuoted = Boolean(display && full && full !== display && full.length > display.length);

  return { display, full, hasQuoted };
}

/** A person's label, however the schema happens to name it: users have `display_name`, customers `name`. */
export function personName(person) {
  if (!person) return null;
  return person.display_name ?? person.name ?? null;
}
